import { ALLOWED_GOTO_TYPES } from '@src/consts';
import {
  behaviorQuestions,
  behaviorRule,
  conditionSourceOptions,
  ifConditionRule,
} from '@src/scenario-editor/validator/node-rules';
import {
  addDefinitionRules,
  createValidator,
  type NodeDefinitionItem,
  type Validator,
} from '@src/scenario-editor/validator/Validator';

/**
 * LF360-2100 — the editor's node validation without the editor.
 *
 * The editor and the server (an AI assistant's scenario draft, activation)
 * must call a graph valid by the same rules. This module is the one place the
 * per-node check and the project lists it depends on are applied: the editor's
 * save hook uses it, and a standalone build of it (`npm run build:validator`)
 * is what the backend runs.
 */

export interface ContentLists {
  ids: string[];
  categories: string[];
  tags: string[];
}

// The project data some node rules are checked against, in the shape the
// editor fetches it: published articles and FitFacts with every category and
// tag, all scenarios, the `task:<id>` node types of the active tasks, and what
// a behaviour rule may name - the project's scenario question texts and tasks.
//
// A list that is not given is not checked. A list that is given and empty
// allows nothing (validateScenarioGraph): a scenario picker in a project with
// no scenarios points at nothing that exists.
export interface ProjectLists {
  fitFacts?: ContentLists;
  articles?: ContentLists;
  scenarioIds?: string[];
  taskNodeTypes?: string[];
  behaviorMessages?: string[];
  behaviorTaskIds?: string[];
}

export interface NodeLike {
  id: string;
  data?: { nodeType?: string } & Record<string, unknown>;
}

export interface NodeFailure {
  id: string;
  success: false;
  errors: unknown;
}

// The value an empty allow-list becomes when it must allow nothing: an enum
// that no real id or text equals. (An empty enum is not a valid schema, and the
// dynamic params drop it, which would allow anything.)
const NOTHING = '\u0000nothing-allowed';
const orNothing = (values: string[]) => (values.length ? values : [NOTHING]);

// `strict`: an empty list allows nothing. The editor leaves it off - it
// cannot tell an empty list from one that has not loaded yet, and would mark
// every task node invalid while the project's tasks are on their way.
export interface ApplyOptions { strict?: boolean }

// Each list goes to the rule that names it; a rule a definition set does not
// have is left alone.
const setParams = (
  validator: Validator,
  schemaName: string,
  params: Record<string, string[]>,
  { strict = false }: ApplyOptions = {},
) => {
  if (!validator.getSchema(schemaName)) { return; }
  const values = strict
    ? Object.fromEntries(Object.entries(params).map(([key, list]) => [key, orNothing(list)]))
    : params;
  validator.updateDynamicParamsForSchema(schemaName, values);
};

export const applyFitFacts = (validator: Validator, lists: ContentLists, options?: ApplyOptions) =>
  setParams(validator, 'fit:facts', { ids: lists.ids, categories: lists.categories, tags: lists.tags }, options);

export const applyArticles = (validator: Validator, lists: ContentLists, options?: ApplyOptions) =>
  setParams(validator, 'message:suggestions', { ids: lists.ids, categories: lists.categories, tags: lists.tags }, options);

export const applyScenarios = (validator: Validator, scenarioIds: string[], options?: ApplyOptions) =>
  setParams(validator, 'scenario:picker', { scenarioValue: scenarioIds }, options);

export const applyTasks = (validator: Validator, taskNodeTypes: string[], options?: ApplyOptions) =>
  setParams(validator, 'task:*', { tasks: taskNodeTypes }, options);

// A go-to may point at any node of the graph that can be jumped to.
export const applyGotoTargets = (validator: Validator, nodes: NodeLike[], options?: ApplyOptions) =>
  setParams(validator, 'message:goto', {
    goto: nodes.filter((node) => ALLOWED_GOTO_TYPES.includes(node.data?.nodeType ?? '')).map((node) => node.id),
  }, options);

// The rules the editor's condition panels register for one node, built for
// every condition node of the graph (validator/node-rules.ts). An IF compares
// against its source node's options; a behaviour rule names questions of the
// project, checked only when those lists are given.
const addConditionRules = (validator: Validator, graph: Record<string, NodeLike>, nodes: NodeLike[], lists: ProjectLists) => {
  const canNameQuestions = lists.behaviorMessages !== undefined && lists.behaviorTaskIds !== undefined;
  for (const node of nodes) {
    const nodeType = node.data?.nodeType;
    if (nodeType === 'if:condition') {
      const source = graph[String(node.data?.condSourceId ?? '')]?.data;
      const options = conditionSourceOptions(source).map((option) => option.value);
      validator.addZodRule(`if:condition:${node.id}`, ifConditionRule(orNothing(options)));
    }
    if (nodeType === 'if:condition-behavior' && canNameQuestions) {
      const questions = behaviorQuestions(nodes, lists.behaviorMessages ?? [], lists.behaviorTaskIds ?? []);
      validator.addZodRule(`if:condition-behavior:${node.id}`, behaviorRule(orNothing(questions)));
    }
  }
};

// The rule of one node: one written for this very node, else its type's.
const ruleResult = (validator: Validator, node: NodeLike) => {
  const nodeType = node.data?.nodeType ?? '';
  if (validator.isRuleImplemented(`${nodeType}:${node.id}`)) {
    return validator.validate(`${nodeType}:${node.id}`, node.data ?? {});
  }
  if (validator.isRuleImplemented(nodeType)) {
    return validator.validate(nodeType, node.data ?? {});
  }
  return null;
};

// The nodes that fail their rule; a node with no rule passes.
export const validateNodes = (validator: Validator, nodes: NodeLike[]): NodeFailure[] =>
  nodes
    .map((node) => ({ ...ruleResult(validator, node), id: node.id }))
    .filter((result) => result && result.success === false) as NodeFailure[];

/**
 * Validates a stored scenario graph (the editor's serialized hash table of
 * nodes) the way the editor does once every panel has been opened: the
 * package's rules, the consumer's node definitions over them, the project
 * lists, and the rule of each condition node. A list given empty allows
 * nothing; a list not given is not checked.
 */
export const validateScenarioGraph = (
  graph: Record<string, NodeLike>,
  definitions: Record<string, NodeDefinitionItem>,
  lists: ProjectLists,
) => {
  const validator = createValidator();
  addDefinitionRules(validator, definitions);

  const strict = { strict: true };
  const nodes = Object.values(graph ?? {});
  if (lists.fitFacts) { applyFitFacts(validator, lists.fitFacts, strict); }
  if (lists.articles) { applyArticles(validator, lists.articles, strict); }
  if (lists.scenarioIds) { applyScenarios(validator, lists.scenarioIds, strict); }
  if (lists.taskNodeTypes) { applyTasks(validator, lists.taskNodeTypes, strict); }
  applyGotoTargets(validator, nodes, strict);
  addConditionRules(validator, graph ?? {}, nodes, lists);

  const failures = validateNodes(validator, nodes);
  return { isValid: failures.length === 0, failures };
};
